import { ApplicationRef, Component, ComponentFactoryResolver, ComponentRef, ElementRef, EventEmitter, Injector, Input, Output } from '@angular/core';
import { Constructor } from '@skylab-shared/types';
import { getComponentRefElement } from '../utils/component-ref-element';
import { $, $Element } from '../utils/element';
import { CreatorStepComponentBase } from './creator-step/creator-step.component-base';
import { CreatorTimelineComponent } from './creator-timeline/creator-timeline.component';

type StepComponent = CreatorStepComponentBase<unknown, unknown>;

const enum CreatorSwitch {
    Previous = -1,
    Default,
    Next,
    Custom
}

export interface CreatorStep {
    readonly id: string;
    readonly title: string;
    readonly component: Constructor<StepComponent>;
    active?: boolean;
    hideInTimeline?: boolean;
    hidden?: boolean;
}

export interface CreatorApi {
    onNextStep: () => void;
    onPreviousStep: () => void;
    changeStep: (step: CreatorStep) => void;
    onSkip: EventEmitter<any>;
    onSave: EventEmitter<any>;
    currentStep: CreatorStep;
    steps: CreatorStep[];
}

@Component({
    selector: 'skl-creator',
    templateUrl: './creator.component.html',
    imports: [CreatorTimelineComponent]
})
export class CreatorComponent {
    @Input() public steps!: CreatorStep[];
    @Input() public context: unknown;
    @Output() public onSave = new EventEmitter<any>();
    @Output() public onSkip = new EventEmitter<any>();
    public currentStep!: CreatorStep;
    public timelineSteps!: CreatorStep[];

    private stepsContainer!: $Element;
    private api!: CreatorApi;

    public constructor(
        private readonly appRef: ApplicationRef,
        private readonly injector: Injector,
        private readonly componentFactoryResolver: ComponentFactoryResolver,
        private readonly elementRef: ElementRef
    ) {}

    public ngAfterContentInit(): void {
        this.steps = this.steps.filter(elem => !elem.hidden);
        this.currentStep = this.steps[this.currentStepIndex];
        this.timelineSteps = [...this.steps.filter(step => !step.hideInTimeline)];
        this.api = this.initApi();
        this.compileComponents();
        this.toggleStep();
    }

    public ngOnDestroy(): void {
        this.stepsComponentsCollection.forEach(componentRef => {
            this.appRef.detachView(componentRef.hostView);
            componentRef.destroy();
        });
    }

    private initApi(): CreatorApi {
        return {
            onNextStep: () => {
                this.changeStep(null, CreatorSwitch.Next);
            },
            onPreviousStep: () => {
                this.changeStep(null, CreatorSwitch.Previous);
            },
            changeStep: (step: CreatorStep) => {
                this.changeStep(step);
            },
            onSkip: this.onSkip,
            onSave: this.onSave,
            currentStep: this.currentStep,
            steps: this.steps
        };
    }

    public changeStep(selectedStep: CreatorStep | null, toggle: CreatorSwitch = CreatorSwitch.Custom): void {
        this.currentStep.active = true;

        if (!selectedStep) {
            this.currentStep = this.api.currentStep = this.steps[this.currentStepIndex + toggle];
            const foundStep = this.steps.find(step => step.id === this.currentStep.id);
            if (foundStep) {
                foundStep.active = true;
            }
        } else if (selectedStep.active) {
            const foundStep = this.steps.find(step => step.id === selectedStep.id);
            if (foundStep) {
                this.currentStep = this.api.currentStep = foundStep;
            }
        }

        this.toggleStep();
    }

    public toggleStep(): void {
        this.stepsContainer.childrens().forEach(elem => elem.hide());
        this.stepsContainer.childrens().find(child => child.getAttr('id') === this.currentStep.id)?.show();
        this.stepsComponentsCollection.find(elem => elem.instance.id === this.currentStep.id)?.instance.onStepActive();
    }

    private get currentStepIndex(): number {
        return this.currentStep ? this.steps.findIndex(x => x.id === this.currentStep.id) : 0;
    }

    public timelineChange(step: CreatorStep): void {
        this.changeStep(step);
    }

    public showTimeline(): boolean {
        return !this.currentStep.hideInTimeline;
    }

    private stepsComponentsCollection: ComponentRef<StepComponent>[] = [];
    private compileComponents(): void {
        if (!this.stepsContainer)
            this.stepsContainer = $(this.elementRef.nativeElement).find('.js-creator-steps-container');

        for (const step of this.steps) {
            const componentRef = this.componentFactoryResolver
                .resolveComponentFactory<StepComponent>(step.component)
                .create(this.injector);

            componentRef.instance.context = this.context;
            componentRef.instance.id = step.id;
            componentRef.instance.api = this.api;

            this.stepsComponentsCollection.push(componentRef);

            this.appRef.attachView(componentRef.hostView);
            componentRef.changeDetectorRef.detectChanges();
            const componentElement = getComponentRefElement(componentRef.hostView);
            componentElement.setAttr('id', step.id);
            this.stepsContainer.append(componentElement.element);
        }
    }
}
